import { useCommunityDetail } from '@communities/community/hooks';
import { useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';

/**
 * Hook to get the current community group ID from URL parameters or community data.
 * Maintains stability during loading states to prevent UI components from disappearing
 * when switching between communities within the same group.
 * @returns The community group ID from the current URL or community data, with fallback to last known value during loading
 */
export function useCurrentCommunityGroupId(): string | undefined {
  const { groupId } = useParams<{ groupId?: string }>();
  const { community, loading } = useCommunityDetail();
  const lastKnownGroupIdRef = useRef<string | undefined>(undefined);

  // Determine the current community group ID based on the current route
  const currentGroupId = groupId || community?.communityGroupId || undefined;

  // Update the last known group ID when we have a valid one and we're not loading
  useEffect(() => {
    if (currentGroupId && !loading) {
      lastKnownGroupIdRef.current = currentGroupId;
    }
  }, [currentGroupId, loading]);

  // Return the current group ID if available, otherwise fall back to the last known one
  return currentGroupId || lastKnownGroupIdRef.current;
}
